import { CheckOutlined, CloseSquareOutlined, CopyOutlined, PlusOutlined } from "@ant-design/icons";
import TemplateDesignerRow from "@Components/assessment/TemplateDesignerRow";
import FeedbackForm from "@Components/shared/FeedbackForm";
import { useGroupsQuery, useTemplatesQuery } from "@Components/shared/queries";
import { IItemRenderProps, SortableList } from "@Components/shared/SortableList";
import { IFeedbackTemplateDetails } from "@Models/IFeedbackTemplateDetails";
import { Col, Divider, Form, Input, InputRef, Popconfirm, Row, Select, Tooltip } from "antd";
import React, { useRef } from "react";
import { getSchema } from "./helpers";
import useTemplateDesigner from "./hooks/useTemplateDesigner";
import { getSystemId, hasCapability } from "@Utils";
import { SystemContext } from "@Constants";

import "@Styles/components/designer.scss";
import IconButton from "@Components/shared/IconButton";

interface IProps {
    templateDetails?: IFeedbackTemplateDetails;
}

const TemplateDesigner: React.FC<IProps> = ({ templateDetails }: IProps) => {
    const nameInputRef = useRef<InputRef>();

    const [form] = Form.useForm();

    const { data: groups } = useGroupsQuery();

    if (hasCapability("Feedback/CreateTemplate", SystemContext))
        groups.unshift({
            groupId: getSystemId(),
            name: "All Groups",
            parentGroupId: getSystemId(),
            ownerId: getSystemId(),
            count: 0,
        });

    const {
        data: groupTemplates,
        isFetched: hasFetchedTemplates,
        isFetching: isFetchingTemplates,
    } = useTemplatesQuery(groups.map((group) => group.groupId));

    const {
        handleAdd,
        handleDebouncedEditFormSubmit,
        handleDeleteRow,
        handleDeleteTemplate,
        handleDuplicateClick,
        handleEditFormSubmit,
        handleNameChange,
        handleNewClick,
        handleOptionArrayChange,
        handleOptionChange,
        handleTemplateClear,
        handleTemplateSelect,
        handleTypeChange,
        items,
        selectedTemplateId,
        setItems,
        template,
    } = useTemplateDesigner({
        form,
        groupTemplates,
        nameInputRef,
        templateDetails,
    });

    const isLoadingTemplates = !hasFetchedTemplates || isFetchingTemplates;

    return (
        <div className={"template-designer designer"}>
            <Row gutter={[8, 16]}>
                <Col md={18} xs={12}>
                    <Form.Item label="Select Existing Template">
                        <Select
                            allowClear
                            disabled={isLoadingTemplates}
                            fieldNames={{ label: "name", value: "templateId" }}
                            loading={isLoadingTemplates}
                            onClear={handleTemplateClear}
                            onSelect={handleTemplateSelect}
                            options={groupTemplates}
                            placeholder={`${isLoadingTemplates ? "Loading Templates..." : "Select a Template"}`}
                            value={!isLoadingTemplates ? selectedTemplateId : undefined}
                        />
                    </Form.Item>
                </Col>
                <Col md={6} xs={12}>
                    <Form.Item label="Actions">
                        <Row>
                            <IconButton className="secondary" onClick={handleNewClick} icon={<PlusOutlined />}>
                                New
                            </IconButton>
                            {template?.templateId && (
                                <IconButton
                                    className="secondary"
                                    onClick={handleDuplicateClick}
                                    icon={<CopyOutlined />}
                                >
                                    Duplicate
                                </IconButton>
                            )}
                        </Row>
                    </Form.Item>
                </Col>
            </Row>
            <Divider />
            <Form
                form={form}
                name="template-edit"
                className="template-edit-form"
                layout="vertical"
                onFinish={() => handleEditFormSubmit(false)}
                onValuesChange={async (changes) => {
                    if ("templateId" in changes || !template.templateId) return; // nothing to update
                    await handleDebouncedEditFormSubmit();
                }}
                initialValues={{
                    name: template?.name,
                    description: template?.description,
                    group: template?.groupId,
                }}
                disabled={template?.locked}
            >
                <Row gutter={[8, 16]}>
                    <Col md={6} xs={12}>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[{ required: true, message: "Please give the template a name" }]}
                        >
                            <Input placeholder="Template name" ref={nameInputRef} />
                        </Form.Item>
                    </Col>
                    <Col md={6} xs={12}>
                        <Form.Item name="description" label="Description">
                            <Input placeholder="Add a description" />
                        </Form.Item>
                    </Col>
                    <Col md={6} xs={12}>
                        <Form.Item
                            name="groupId"
                            label="Group"
                            rules={[{ required: true, message: "Please select a group" }]}
                        >
                            <Select
                                placeholder="Groups"
                                allowClear
                                options={groups}
                                fieldNames={{
                                    label: "name",
                                    value: "groupId",
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={6} xs={12}>
                        <Form.Item label="Actions">
                            <Row style={{ gap: 5 }}>
                                <IconButton className="secondary" htmlType="submit" icon={<CheckOutlined />}>
                                    {template.templateId ? "Update" : "Save"}
                                </IconButton>
                                <Popconfirm
                                    title={`Are you sure that you want to ${
                                        template.templateId
                                            ? `delete ${form.getFieldValue("name")}`
                                            : "clear this Template?"
                                    }`}
                                    onConfirm={handleDeleteTemplate}
                                    cancelButtonProps={{ className: "secondary" }}
                                >
                                    <Tooltip
                                        title={template?.locked ? "You can't delete a template that is in use." : null}
                                    >
                                        <IconButton
                                            disabled={template?.locked}
                                            className="secondary danger"
                                            icon={<CloseSquareOutlined />}
                                        >
                                            {template.templateId ? "Delete" : "Clear"}
                                        </IconButton>
                                    </Tooltip>
                                </Popconfirm>
                            </Row>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            {!template?.locked ? (
                <div className={"template-designer-item-list designer-item-list"}>
                    <SortableList
                        items={[...items]}
                        setItems={setItems}
                        itemRender={({ item }: IItemRenderProps) => {
                            const schema = getSchema(item?.id, template?.fields);
                            return (
                                <TemplateDesignerRow
                                    key={`${template?.templateId}_${schema?.id}`}
                                    handleDelete={handleDeleteRow}
                                    handleNameChange={handleNameChange}
                                    handleOptionArrayChange={handleOptionArrayChange}
                                    handleOptionChange={handleOptionChange}
                                    handleTypeChange={handleTypeChange}
                                    schema={schema}
                                />
                            );
                        }}
                    />
                    {template?.fields?.length < 50 && (
                        <div className={"template-designer-add-control designer-add-control"} onClick={handleAdd}>
                            <PlusOutlined />
                        </div>
                    )}
                </div>
            ) : (
                <FeedbackForm isDisabled {...template} />
            )}
        </div>
    );
};
export default TemplateDesigner;
